import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../utils/cn';

export interface RateOption {
  value: string;
  label: string;
  description: string;
  icon?: React.ReactNode;
}

export interface RateSelectorProps {
  options: RateOption[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
}

// Default rate options with icons and colors
const rateStyles: Record<string, { bg: string; border: string; iconBg: string; text: string }> = {
  lower: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-300',
    iconBg: 'bg-emerald-100',
    text: 'text-emerald-700',
  },
  same: {
    bg: 'bg-blue-50',
    border: 'border-blue-300',
    iconBg: 'bg-blue-100',
    text: 'text-blue-700',
  },
  higher: {
    bg: 'bg-violet-50',
    border: 'border-violet-300',
    iconBg: 'bg-violet-100',
    text: 'text-violet-700',
  },
};

const defaultIcons: Record<string, React.ReactNode> = {
  lower: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
    </svg>
  ),
  same: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
    </svg>
  ),
  higher: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
    </svg>
  ),
};

const RateSelector: React.FC<RateSelectorProps> = ({
  options,
  value,
  onChange,
  label,
  error,
  disabled = false,
  className,
}) => {
  return (
    <div className={cn('space-y-3', className)}>
      {label && (
        <label className="text-sm font-medium text-secondary-700 block">
          {label}
        </label>
      )}

      <div className="grid grid-cols-1 gap-3">
        {options.map((option) => {
          const isSelected = value === option.value;
          const style = rateStyles[option.value] || rateStyles.same;
          const icon = option.icon || defaultIcons[option.value];

          return (
            <motion.button
              key={option.value}
              type="button"
              onClick={() => !disabled && onChange(option.value)}
              disabled={disabled}
              className={cn(
                'relative w-full text-left p-4 rounded-xl border-2 transition-all duration-200',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                'flex items-center gap-4',
                isSelected
                  ? `${style.bg} ${style.border} shadow-md`
                  : 'bg-white border-secondary-200 hover:border-secondary-300 hover:shadow-sm',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
              whileHover={!disabled ? { scale: 1.01 } : {}}
              whileTap={!disabled ? { scale: 0.99 } : {}}
            >
              {/* Radio indicator */}
              <div className={cn(
                'flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all',
                isSelected
                  ? `${style.border} ${style.iconBg}`
                  : 'border-secondary-300'
              )}>
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className={cn('w-2.5 h-2.5 rounded-full', style.text.replace('text-', 'bg-'))}
                  />
                )}
              </div>

              {/* Icon */}
              <div className={cn(
                'flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center',
                isSelected ? style.iconBg : 'bg-secondary-100',
                isSelected ? style.text : 'text-secondary-500'
              )}>
                {icon}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className={cn(
                  'font-semibold text-sm md:text-base',
                  isSelected ? style.text : 'text-secondary-700'
                )}>
                  {option.label}
                </p>
                <p className={cn(
                  'text-xs md:text-sm mt-0.5',
                  isSelected ? style.text.replace('700', '600') : 'text-secondary-500'
                )}>
                  {option.description}
                </p>
              </div>

              {/* Selection checkmark */}
              {isSelected && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className={cn(
                    'flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center',
                    style.text.replace('text-', 'bg-')
                  )}
                >
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </motion.div>
              )}
            </motion.button>
          );
        })}
      </div>

      {error && (
        <p className="text-sm text-red-600 flex items-center gap-1">
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
};

export { RateSelector };
